/**
 * Mobile arc Part B — API base resolution.
 *
 * On the web the SPA is same-origin with `api/index.js`, so every call
 * site uses relative `/api/...` paths and this is a no-op (VITE_API_BASE
 * is unset → ''). Inside the Capacitor shell the origin is
 * `capacitor://localhost` — relative paths point at nothing — so
 * `npm run build:cap` bakes VITE_API_BASE (the dev-branch preview URL by
 * default) and every API call resolves against it.
 *
 * Contract: pass API *paths* ('/api/…'). Absolute URLs pass through
 * untouched so callers that already hold a full URL keep working.
 */

import { isNativeApp } from './platform';

const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

/** The canonical public host, used when the app has to build a URL that
 *  LEAVES the app — an invite link a coach copies into Messages, say. */
const CANONICAL_HOST = 'https://tayro.run';

export function apiUrl(path: string): string {
  if (!API_BASE || !path.startsWith('/')) return path;
  return `${API_BASE}${path}`;
}

/**
 * FIELD-TEST BUG 2 — the origin a SHAREABLE link must be built on.
 *
 * `window.location.origin` is the wrong answer inside the Capacitor shell:
 * the WebView origin there is `capacitor://localhost` (Android: `https://
 * localhost` — both allow-listed by the API at Wave 18), so the coach's
 * "Copy link" handed athletes `capacitor://localhost/v2/landing?join=…`,
 * which resolves nowhere off the coach's own phone.
 *
 * On the WEB the deployed origin IS the right host — localhost, the Vercel
 * preview, and prod each want their own — so web behaviour is unchanged.
 * On NATIVE we fall back to the same constant the API calls resolve against
 * (`VITE_API_BASE`, baked by `build:cap` — prod `https://tayro.run`, dev the
 * preview URL), and to the canonical host when that is unset.
 *
 * NEVER read `window.location.origin` directly to build a shareable URL.
 */
export function canonicalOrigin(): string {
  if (!isNativeApp() && typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return API_BASE || CANONICAL_HOST;
}
